(function () {
  'use strict';

  var initializedSections = new WeakMap();
  var initializedMediaSections = new WeakMap();
  var assignmentCache = {};
  var assignmentAnalyticsPublished = {};

  function parseJsonScript(script, warningMessage) {
    if (!script) {
      return null;
    }

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[product-ab-test]', warningMessage, error);
      return null;
    }
  }

  function sanitizeCookiePart(value) {
    return String(value || '')
      .trim()
      .replace(/[^a-zA-Z0-9_-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function normalizeTestConfig(config) {
    if (!config) {
      return null;
    }

    var products = Array.isArray(config.products) ? config.products.filter(Boolean) : [];
    var variants = Array.isArray(config.variants) ? config.variants.filter(Boolean) : [];

    // A no-swap test shows the same product to every half and changes only the
    // page, so it has one product and as many variants as were asked for. A
    // normal test takes its variants from the product list, which is why it
    // needs two.
    if (config.noSwap) {
      if (products.length < 1 || variants.length < 2) {
        return null;
      }
    } else if (products.length < 2) {
      return null;
    }

    var normalized = {};
    Object.keys(config).forEach(function (key) {
      normalized[key] = config[key];
    });

    normalized.products = products;
    normalized.variants = variants.length ? variants : products.map(function (item) {
      return item && item.variant;
    }).filter(Boolean);
    normalized.testId = normalized.testId || (products[0] && products[0].handle);
    normalized.cookieName = normalized.cookieName || ('elanora_ab_' + sanitizeCookiePart(normalized.testId));
    normalized.cookieDays = normalized.cookieDays || 30;
    normalized.canonicalProductHandle = normalized.canonicalProductHandle || (products[0] && products[0].handle);
    normalized.canonicalPath = normalized.canonicalPath || (products[0] && products[0].path);

    return normalized;
  }

  function getGlobalTests() {
    var globalConfig = parseJsonScript(
      document.querySelector('[data-ab-tests-config]'),
      'Invalid global config JSON'
    );
    var tests = globalConfig && Array.isArray(globalConfig.tests) ? globalConfig.tests : [];

    return tests
      .map(normalizeTestConfig)
      .filter(Boolean);
  }

  function parseLegacySectionConfig(section) {
    return normalizeTestConfig(parseJsonScript(
      section.querySelector('[data-ab-test-config]'),
      'Invalid section config JSON'
    ));
  }

  function getCookie(name) {
    var prefix = name + '=';
    var parts = document.cookie ? document.cookie.split('; ') : [];

    for (var i = 0; i < parts.length; i += 1) {
      if (parts[i].indexOf(prefix) === 0) {
        return decodeURIComponent(parts[i].slice(prefix.length));
      }
    }

    return '';
  }

  function setCookie(name, value, days) {
    var expires = '';
    var duration = parseInt(days, 10);

    if (duration > 0) {
      var date = new Date();
      date.setTime(date.getTime() + duration * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }

    document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax';
  }

  // Every half of a no-swap test points at the same product, so the candidates
  // are built from the variant labels rather than from the product list. The
  // shape matches a swapping test's entry, so the cookie, the analytics and the
  // caller cannot tell the two kinds apart.
  function getVariantCandidates(config) {
    var products = Array.isArray(config.products) ? config.products : [];

    if (!config.noSwap) {
      /*
       * KK-1420. A shell canonical is in the product list so the section
       * can find its test by handle and so its own links are protected,
       * but it is not a half of the test: its page is priced for neither
       * offer. It carries no letter, and only lettered products can be
       * assigned. Every test written before this letters all of them, so
       * the filter passes them through unchanged.
       */
      return products.filter(function (item) {
        return item && item.variant;
      });
    }

    var canonical = products[0];
    if (!canonical) {
      return [];
    }

    return (config.variants || []).map(function (variant) {
      var candidate = {};
      Object.keys(canonical).forEach(function (key) {
        candidate[key] = canonical[key];
      });
      candidate.variant = variant;
      return candidate;
    });
  }

  function chooseVariant(config) {
    var cookieName = config.cookieName || ('elanora_ab_' + sanitizeCookiePart(config.testId));
    var savedVariant = getCookie(cookieName);
    var products = getVariantCandidates(config);
    var existing = products.find(function (item) {
      return item && item.variant === savedVariant;
    });

    if (existing) {
      return existing;
    }

    var assignedIndex = Math.floor(Math.random() * products.length);
    var assigned = products[assignedIndex] || products[0];

    if (assigned && assigned.variant) {
      setCookie(cookieName, assigned.variant, config.cookieDays || 30);
    }

    return assigned;
  }

  function getAssignment(config) {
    var key = config.cookieName || config.testId;
    if (!assignmentCache[key]) {
      assignmentCache[key] = chooseVariant(config);
    }

    return assignmentCache[key];
  }

  function buildPayload(config, assignment, eventName) {
    return {
      event: eventName,
      ab_test_id: config.testId,
      ab_variant: assignment.variant,
      ab_product_handle: assignment.handle,
      canonical_product_handle: config.canonicalProductHandle,
      canonical_path: config.canonicalPath
    };
  }

  function getShopifyCustomerEventName(eventName) {
    var eventNames = {
      product_ab_test_assigned: 'elanora:ab_test_assigned',
      product_ab_test_offer_view: 'elanora:ab_test_offer_viewed',
      product_ab_test_add_to_cart: 'elanora:ab_test_add_to_cart'
    };

    return eventNames[eventName] || ('elanora:' + eventName);
  }

  function publishShopifyCustomerEvent(config, assignment, eventName, payload) {
    var shopifyEventName = getShopifyCustomerEventName(eventName);

    if (!window.Shopify || !window.Shopify.analytics || typeof window.Shopify.analytics.publish !== 'function') {
      return;
    }

    try {
      window.Shopify.analytics.publish(shopifyEventName, {
        source_event: eventName,
        ab_test_id: config.testId,
        ab_variant: assignment.variant,
        ab_product_handle: assignment.handle,
        canonical_product_handle: config.canonicalProductHandle,
        canonical_path: config.canonicalPath,
        data_layer_payload: payload
      });
    } catch (error) {
      console.warn('[product-ab-test]', 'Unable to publish Shopify customer event', error);
    }
  }

  function pushAnalytics(config, assignment, eventName) {
    var payload = buildPayload(config, assignment, eventName);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    publishShopifyCustomerEvent(config, assignment, eventName, payload);
  }

  function publishAssignmentOnce(config, assignment) {
    var key = config.cookieName || config.testId;
    if (assignmentAnalyticsPublished[key]) {
      return;
    }

    assignmentAnalyticsPublished[key] = true;
    pushAnalytics(config, assignment, 'product_ab_test_assigned');
  }

  // KK-1623. The GA4 user properties that say which half a visitor is in are
  // written only by a test's own page, never by its product cards elsewhere.
  // They used to be written from every card too, and the three keys are shared
  // by all tests, so whichever test a page showed last took the visitor over:
  // the neck cream test counted 29 025 users over a fortnight in which 1 432
  // opened its page. The slot key gives every test a key of its own, so a
  // shopper who sees two tests stays in both. Product and media tests number
  // their slots separately, hence the two prefixes.
  function reportArm(config, assignment) {
    if (!config || !assignment || typeof window.gtag !== 'function') {
      return;
    }

    try {
      var properties = {
        product_ab_test_id: config.testId,
        product_ab_variant: assignment.variant,
        product_ab_product_handle: assignment.handle
      };

      if (config.slot) {
        // GA4 keeps 36 characters of a value; shorten the id, never the letter.
        var letter = ':' + assignment.variant;
        properties[(config.isMedia ? 'ab_media_slot_' : 'ab_slot_') + config.slot] =
          String(config.testId).slice(0, 36 - letter.length) + letter;
      }

      window.gtag('set', 'user_properties', properties);
    } catch (error) {
      console.warn('[product-ab-test]', 'Unable to report the test half to analytics', error);
    }
  }

  function productBelongsToTest(config, handle) {
    return Array.isArray(config.products) && config.products.some(function (item) {
      return item && item.handle === handle;
    });
  }

  function findFirstTestForProduct(tests, handle) {
    if (!handle) {
      return null;
    }

    for (var i = 0; i < tests.length; i += 1) {
      if (productBelongsToTest(tests[i], handle)) {
        return tests[i];
      }
    }

    return null;
  }

  function cloneConfig(config) {
    var cloned = {};
    Object.keys(config).forEach(function (key) {
      cloned[key] = config[key];
    });
    return cloned;
  }

  function getSectionConfig(section, tests) {
    var legacyConfig = parseLegacySectionConfig(section);
    var currentProductHandle;
    var config;

    if (legacyConfig) {
      config = cloneConfig(legacyConfig);
    } else {
      currentProductHandle = section.dataset.abCurrentProductHandle || section.dataset.currentProductHandle || '';
      config = tests.find(function (test) {
        return test.testId === section.dataset.abTestId && productBelongsToTest(test, currentProductHandle);
      }) || findFirstTestForProduct(tests, currentProductHandle);

      if (!config) {
        return null;
      }

      config = cloneConfig(config);
    }

    config.sectionId = section.dataset.section || config.sectionId;
    config.currentProductHandle = section.dataset.abCurrentProductHandle ||
      section.dataset.currentProductHandle ||
      config.currentProductHandle;
    config.canonicalProductHandle = section.dataset.canonicalProductHandle ||
      config.canonicalProductHandle;
    config.canonicalPath = section.dataset.canonicalPath ||
      config.canonicalPath;

    return config;
  }

  function replaceProductLinks(root, config) {
    var products = Array.isArray(config.products) ? config.products : [];
    var paths = products
      .map(function (item) { return item && item.path; })
      .filter(Boolean);

    root.querySelectorAll('a[href]').forEach(function (link) {
      var href = link.getAttribute('href');
      if (!href) return;

      var shouldProtect = link.hasAttribute('data-ab-protected-link') ||
        paths.some(function (path) {
          return href === path || href.indexOf(path + '?') === 0 || href.indexOf(path + '#') === 0;
        });

      if (shouldProtect) {
        link.setAttribute('href', config.canonicalPath);
        link.setAttribute('data-ab-protected-link', '');
      }
    });
  }

  function updateCardLinks(card, config) {
    if (!config.canonicalPath) {
      return;
    }

    card.querySelectorAll('a[href]').forEach(function (link) {
      link.setAttribute('href', config.canonicalPath);
      link.setAttribute('data-ab-protected-link', '');
    });

    card.querySelectorAll('[data-product-url]').forEach(function (element) {
      element.setAttribute('data-product-url', config.canonicalPath);
    });
  }

  function upsertHiddenInput(form, name, value) {
    var selector = 'input[type="hidden"][name="' + name.replace(/"/g, '\\"') + '"]';
    var input = form.querySelector(selector);

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }

    input.value = value || '';
    input.setAttribute('data-ab-test-property', '');
  }

  function getPropertyFields(config, assignment) {
    return [
      { fieldName: '_ab_test_id', value: config.testId },
      { fieldName: '_ab_variant', value: assignment.variant },
      { fieldName: '_ab_product_handle', value: assignment.handle },
      { fieldName: '_ab_canonical_path', value: config.canonicalPath }
    ];
  }

  function annotateForms(root, config, assignment) {
    var fields = getPropertyFields(config, assignment);

    root.querySelectorAll('form[action*="/cart/add"]').forEach(function (form) {
      fields.forEach(function (field) {
        upsertHiddenInput(form, 'properties[' + field.fieldName + ']', field.value);
      });
    });
  }

  function decorateProductForms(root, config, assignment) {
    root.querySelectorAll('product-form').forEach(function (productForm) {
      var fields = getPropertyFields(config, assignment);
      var existingFields = Array.prototype.slice.call(productForm.customFields || []);
      var nonAbFields = existingFields.filter(function (field) {
        return !field || !field.fieldName || field.fieldName.indexOf('_ab_') !== 0;
      });

      productForm.customFields = nonAbFields.concat(fields);
    });
  }

  // reportsArm is true only where the form sits on the test's own page; a card
  // elsewhere still reports its add to cart to dataLayer but leaves GA4 alone.
  function bindFormAnalytics(root, config, assignment, reportsArm) {
    root.querySelectorAll('form[action*="/cart/add"]').forEach(function (form) {
      if (form.dataset.abAnalyticsBound === 'true') return;

      form.dataset.abAnalyticsBound = 'true';
      form.addEventListener('submit', function () {
        annotateForms(root, config, assignment);
        decorateProductForms(root, config, assignment);
        pushAnalytics(config, assignment, 'product_ab_test_add_to_cart');
        if (reportsArm) {
          reportArm(config, assignment);
        }
      }, true);
    });
  }

  function updateFormProductTargets(root, offerTemplate) {
    var variantId = offerTemplate && offerTemplate.dataset.variantId;
    var productId = offerTemplate && offerTemplate.dataset.productId;

    if (!variantId && !productId) {
      return;
    }

    root.querySelectorAll('form[action*="/cart/add"]').forEach(function (form) {
      var variantInput = form.querySelector('input[name="id"]');
      var productIdInput = form.querySelector('input[name="product-id"]');

      if (variantInput && variantId) {
        variantInput.value = variantId;
        variantInput.dataset.value = variantId;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (productIdInput && productId) {
        productIdInput.value = productId;
      }
      if (form.dataset.productId && productId) {
        form.dataset.productId = productId;
      }
    });
  }

  function prepareAssignedSection(section, config, assignment) {
    var productInfo = section.querySelector('#ProductInfo-' + config.sectionId);

    if (productInfo && config.canonicalPath) {
      productInfo.dataset.url = config.canonicalPath;
    }

    section.setAttribute('data-ab-active-variant', assignment.variant);
    section.setAttribute('data-ab-active-product-handle', assignment.handle);
    replaceProductLinks(section, config);
    annotateForms(section, config, assignment);
    decorateProductForms(section, config, assignment);
    bindFormAnalytics(section, config, assignment, true);
    pushAnalytics(config, assignment, 'product_ab_test_offer_view');
  }

  function findOfferTemplate(section, assignment) {
    var selector = 'template[data-ab-offer-template="' +
      String(assignment.handle || '').replace(/"/g, '\\"') + '"]';
    return section.querySelector(selector);
  }

  function findCardOfferTemplate(card, assignment) {
    var selector = 'template[data-ab-card-offer-template="' +
      String(assignment.handle || '').replace(/"/g, '\\"') + '"]';
    return card.querySelector(selector);
  }

  function getTemplateRoot(template) {
    return template && template.content ? template.content : template;
  }

  function replaceInnerMarkup(current, offered) {
    if (current && offered) {
      current.innerHTML = offered.innerHTML;
    }
  }

  function replaceElement(current, offered) {
    if (current && offered) {
      current.replaceWith(offered.cloneNode(true));
    }
  }

  function updateOfferForms(section, config, offerTemplate) {
    var variantId = offerTemplate.dataset.variantId;
    var productId = offerTemplate.dataset.productId;
    var formSelectors = [
      '#product-form-' + config.sectionId,
      '#product-form-installment-' + config.sectionId
    ];

    formSelectors.forEach(function (selector) {
      var form = section.querySelector(selector);
      if (!form) return;

      var variantInput = form.querySelector('input[name="id"]');
      var productIdInput = form.querySelector('input[name="product-id"]');

      if (variantInput && variantId) {
        var variantChanged = variantInput.value !== variantId;
        variantInput.value = variantId;
        if (variantChanged) {
          variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      if (productIdInput && productId) {
        productIdInput.value = productId;
      }
      if (form.dataset.productId && productId) {
        form.dataset.productId = productId;
      }
    });
  }

  function updateProductTargets(section, config, offerTemplate) {
    var productInfo = section.querySelector('#ProductInfo-' + config.sectionId);
    var pickupAvailability = section.querySelector('pickup-availability');
    var addButton = section.querySelector('#ProductSubmitButton-' + config.sectionId);
    var stickyAtc = section.querySelector('#sticky-atc-' + config.sectionId);
    var stickyButton = section.querySelector('#StickyAtcSubmitButton-' + config.sectionId);
    var isAvailable = offerTemplate.dataset.available === 'true';
    var hasPickupAvailability = offerTemplate.dataset.pickupAvailable === 'true';

    if (productInfo && config.canonicalPath) {
      productInfo.dataset.url = config.canonicalPath;
    }
    if (pickupAvailability && offerTemplate.dataset.variantId) {
      pickupAvailability.dataset.variantId = offerTemplate.dataset.variantId;
      pickupAvailability.dataset.hasOnlyDefaultVariant = offerTemplate.dataset.hasOnlyDefaultVariant;

      if (hasPickupAvailability && typeof pickupAvailability.fetchAvailability === 'function') {
        if (!pickupAvailability.errorHtml) {
          var pickupTemplate = pickupAvailability.querySelector('template');
          if (pickupTemplate && pickupTemplate.content.firstElementChild) {
            pickupAvailability.errorHtml = pickupTemplate.content.firstElementChild.cloneNode(true);
            pickupAvailability.onClickRefreshList = pickupAvailability.onClickRefreshList.bind(pickupAvailability);
          }
        }
        pickupAvailability.setAttribute('available', '');
        pickupAvailability.fetchAvailability(offerTemplate.dataset.variantId);
      } else {
        pickupAvailability.removeAttribute('available');
        pickupAvailability.innerHTML = '';
        var pickupDrawer = document.querySelector('pickup-availability-drawer');
        if (pickupDrawer) {
          pickupDrawer.remove();
        }
      }
    }
    if (addButton) {
      addButton.disabled = !isAvailable;
    }
    if (stickyButton) {
      stickyButton.disabled = !isAvailable;
    }
    if (stickyAtc) {
      stickyAtc.hidden = !isAvailable;
      stickyAtc.setAttribute('aria-hidden', isAvailable ? 'false' : 'true');
      stickyAtc.style.display = isAvailable ? '' : 'none';
    }
  }

  function updateQuantityBreaks(section, config, offerContent) {
    var current = section.querySelector('#quantity-breaks-' + config.sectionId);
    var offered = offerContent.querySelector('[data-ab-quantity-breaks] quantity-breaks');
    if (!current || !offered) return;

    var updated = offered.cloneNode(true);
    current.replaceWith(updated);

    var productFormElement = section.querySelector('#product-form-' + config.sectionId);
    var productForm = productFormElement && productFormElement.closest('product-form');
    if (productForm) {
      productForm.quantityBreaks = updated;
    }
  }

  /*
   * Hand the offer over from the theme's quantity selector to the challenger's
   * tier selector, and say whether that happened.
   *
   * CSS has already put the tier selector on screen before the first paint, but
   * two things are still true on the control's page: the quantity breaks
   * element is what product-form.js builds its line items from, and it would
   * quietly overwrite everything the tier selector sets, and the tier selector's
   * own inputs are still disabled. So the quantity breaks go, reference and all,
   * and the selector is told to look at the page again.
   */
  function activateOfferTiers(section, config, assignment) {
    /*
     * Prefer the dormant offer that names the challenger being assigned.
     *
     * A control template can carry more than one dormant offer, one per test
     * that runs against it, and from KK-1420 one per half of a single test.
     * The bare selector below takes whichever comes first in the document,
     * which is not necessarily the one this slot assigned, so with two of
     * them the shopper would be shown the other half's offer.
     *
     * The fallback is what keeps this safe to ship: no block written before
     * this change carries a handle, so every one of them still takes the
     * second branch and behaves exactly as it did.
     */
    var tiers = null;
    var handle = assignment && assignment.handle;

    if (handle) {
      tiers = section.querySelector('[data-ab-offer-tiers="' +
        String(handle).replace(/"/g, '\\"') + '"]');
    }

    if (!tiers) {
      tiers = section.querySelector('[data-ab-offer-tiers]');
    }

    if (!tiers) {
      return false;
    }

    /*
     * KK-1420. Every other dormant offer leaves the document, for the same
     * reason the quantity breaks do below: hiding is not enough. Its tier
     * properties are plain inputs carrying the product form's id, so a
     * hidden block still posts _offer_tier_map, _offer_gifts and
     * _offer_tier_titles alongside the ones the shopper actually chose, and
     * the line would arrive with two offers written on it.
     */
    var dormantOffers = section.querySelectorAll('[data-ab-offer-tiers]');
    Array.prototype.forEach.call(dormantOffers, function (other) {
      if (other !== tiers) {
        other.remove();
      }
    });

    /*
     * KK-2149. The page's own tier selector goes as well. Since KK-1827 a
     * control page can carry tiers that are its own offer rather than a
     * dormant one, and the challenger's half must not keep them: CSS in
     * main-product.liquid hides them from the first paint, but their inputs
     * are live and carry the product form's id, so the line would arrive with
     * the control's tier map and gifts written next to the challenger's. Only
     * a challenger's half gets here, so the control's half and every page
     * without a test keep their own offer.
     */
    var ownOffers = section.querySelectorAll('custom-offer-tiers:not([data-ab-offer-tiers])');
    Array.prototype.forEach.call(ownOffers, function (own) {
      own.remove();
    });

    var quantityBreaks = section.querySelector('#quantity-breaks-' + config.sectionId);
    if (quantityBreaks) {
      var productFormElement = section.querySelector('#product-form-' + config.sectionId);
      var productForm = productFormElement && productFormElement.closest('product-form');
      if (productForm) {
        productForm.quantityBreaks = null;
      }
      quantityBreaks.remove();
    }

    if (typeof tiers.refresh === 'function') {
      tiers.refresh();
    }

    return true;
  }

  function activateOffer(section, config, assignment) {
    var offerTemplate = findOfferTemplate(section, assignment);

    if (!offerTemplate) {
      console.warn('[product-ab-test]', 'Falling back to current product offer: missing embedded offer', assignment.handle);
      prepareAssignedSection(section, config, assignment);
      return;
    }

    var offerContent = offerTemplate.content.cloneNode(true);
    var price = section.querySelector('#price-' + config.sectionId + ' .product-info-price-wrapper .price');
    var offeredPrice = offerContent.querySelector('[data-ab-main-price] .price');
    var stickyPrice = section.querySelector('#atc-price-' + config.sectionId);
    var offeredStickyPrice = offerContent.querySelector('[data-ab-sticky-price]');

    replaceElement(price, offeredPrice);
    replaceInnerMarkup(stickyPrice, offeredStickyPrice);
    if (!activateOfferTiers(section, config, assignment)) {
      updateQuantityBreaks(section, config, offerContent);
    }
    updateOfferForms(section, config, offerTemplate);
    updateProductTargets(section, config, offerTemplate);
    prepareAssignedSection(section, config, assignment);
    section.dispatchEvent(new CustomEvent('elanora:ab-offer-activated', { bubbles: true }));
  }

  function applyCardOffer(card, config, assignment) {
    var offerTemplate = findCardOfferTemplate(card, assignment);
    var offerContent = getTemplateRoot(offerTemplate);
    var offeredPrice = offerContent && offerContent.querySelector('[data-ab-card-offer-price]');
    var priceTarget = card.querySelector('[data-ab-card-price]');
    var badgeTargets = card.querySelectorAll('[data-ab-card-badge]');

    if (priceTarget && offeredPrice) {
      priceTarget.innerHTML = offeredPrice.innerHTML;
    }

    // A card carries its badge twice, once worded and once as a bare
    // percentage, and shows whichever suits its layout. Each container says
    // which it is, so each gets the offer in its own wording rather than both
    // getting whichever flavour happened to be rendered first.
    badgeTargets.forEach(function (badgeTarget) {
      var flavour = badgeTarget.getAttribute('data-ab-card-badge');
      var offeredBadge = offerContent && (
        (flavour && offerContent.querySelector('[data-ab-card-offer-badge="' + flavour + '"]')) ||
        offerContent.querySelector('[data-ab-card-offer-badge]')
      );

      badgeTarget.innerHTML = offeredBadge ? offeredBadge.innerHTML : '';
    });

    card.setAttribute('data-ab-active-variant', assignment.variant);
    card.setAttribute('data-ab-active-product-handle', assignment.handle);
    updateCardLinks(card, config);

    if (offerTemplate) {
      updateFormProductTargets(card, offerTemplate);
    }

    annotateForms(card, config, assignment);
    decorateProductForms(card, config, assignment);
    bindFormAnalytics(card, config, assignment);
  }

  function selectAll(root, selector) {
    var items = [];

    if (root && typeof root.matches === 'function' && root.matches(selector)) {
      items.push(root);
    }

    if (root && typeof root.querySelectorAll === 'function') {
      items = items.concat(Array.prototype.slice.call(root.querySelectorAll(selector)));
    }

    return items;
  }

  function initSection(section, tests) {
    if (!section) {
      return;
    }

    var config = getSectionConfig(section, tests);
    if (!config) {
      return;
    }

    // Only the canonical product's URL is split. A challenger's own URL is a
    // landing page in its own right, and it is where the paid traffic lands, so
    // it always shows itself: assigning a visitor there would hand half of that
    // spend the control's offer on a page built to sell the other one. No
    // cookie is read or written here either, so paid arrivals cannot skew the
    // split on the canonical page.
    if (config.currentProductHandle && config.currentProductHandle !== config.canonicalProductHandle) {
      return;
    }

    var sectionKey = [config.testId, config.currentProductHandle, config.sectionId].join('|');
    if (initializedSections.get(section) === sectionKey) {
      return;
    }
    initializedSections.set(section, sectionKey);

    var assignment = getAssignment(config);
    if (!assignment) {
      return;
    }

    publishAssignmentOnce(config, assignment);
    // Every time, not once: a card of this test that initialised earlier may
    // already have used the once flag above.
    reportArm(config, assignment);

    // A no-swap test has already been applied by the time this runs: the head
    // snippet put the variant on <html> and the page rendered itself around it,
    // so nothing on the page is swapped or moved here.
    //
    // The measurement still has to be attached, though, and for a while it was
    // not. Reporting the assignment alone leaves the test readable on revenue,
    // because the assignment sets a GA4 user property that sticks to the
    // visitor through purchase, but it never fires an add to cart event and
    // never writes the variant onto the cart line. So add to cart rate per
    // half, which is one of the metrics the tests are judged on, could not be
    // produced at all, and a finished test could not be re-checked against the
    // orders it actually produced.
    if (config.noSwap) {
      section.setAttribute('data-ab-active-variant', assignment.variant);
      annotateForms(section, config, assignment);
      decorateProductForms(section, config, assignment);
      bindFormAnalytics(section, config, assignment, true);
      pushAnalytics(config, assignment, 'product_ab_test_offer_view');
      return;
    }

    if (assignment.handle === config.currentProductHandle) {
      prepareAssignedSection(section, config, assignment);
      return;
    }

    activateOffer(section, config, assignment);
  }

  function initCards(root, tests) {
    selectAll(root || document, '[data-ab-product-card]').forEach(function (card) {
      var productHandle = card.dataset.abProductHandle || card.dataset.productHandle;
      var config = card.dataset.abTestId ?
        tests.find(function (test) {
          return test.testId === card.dataset.abTestId && productBelongsToTest(test, productHandle);
        }) :
        findFirstTestForProduct(tests, productHandle);

      // A no-swap test sells the same product at the same price in both halves,
      // so its cards have nothing to swap. Leaving them alone also keeps a
      // collection page from bucketing a visitor who never reached the product.
      if (!config || config.noSwap) {
        return;
      }

      var assignment = getAssignment(config);
      if (!assignment) {
        return;
      }

      publishAssignmentOnce(config, assignment);
      applyCardOffer(card, config, assignment);
    });
  }

  function getGlobalMediaTests() {
    var globalConfig = parseJsonScript(
      document.querySelector('[data-ab-tests-config]'),
      'Invalid global config JSON'
    );
    var mediaTests = globalConfig && Array.isArray(globalConfig.mediaTests) ? globalConfig.mediaTests : [];

    return mediaTests.filter(function (test) {
      return test && test.testId;
    });
  }

  function normalizeMediaConfig(test) {
    var config = cloneConfig(test);

    config.cookieName = config.cookieName || ('elanora_ab_' + sanitizeCookiePart(config.testId));
    config.cookieDays = config.cookieDays || 30;
    config.canonicalProductHandle = config.canonicalProductHandle || config.productHandle;
    config.canonicalPath = config.canonicalPath || '';
    config.isMedia = true;
    config.products = [
      { variant: 'A', handle: config.productHandle, path: config.canonicalPath },
      { variant: 'B', handle: config.productHandle, path: config.canonicalPath }
    ];

    return config;
  }

  function findMediaTestById(mediaTests, testId) {
    for (var i = 0; i < mediaTests.length; i += 1) {
      if (mediaTests[i].testId === testId) {
        return mediaTests[i];
      }
    }

    return mediaTests.length === 1 ? mediaTests[0] : null;
  }

  function activateMediaTest(section, config, assignment) {
    // The visible gallery is chosen before paint by the inline script plus CSS
    // in main-product.liquid, so no DOM swap is needed here. This pass only
    // attributes the assignment for analytics and cart line properties.
    section.setAttribute('data-ab-media-active-variant', assignment.variant);
    annotateForms(section, config, assignment);
    decorateProductForms(section, config, assignment);
    bindFormAnalytics(section, config, assignment, true);
    pushAnalytics(config, assignment, 'product_ab_test_offer_view');
  }

  function initMediaSection(section, mediaTests) {
    var test = findMediaTestById(mediaTests, section.getAttribute('data-ab-media-test-id'));
    if (!test) {
      return;
    }

    var config = normalizeMediaConfig(test);
    var sectionKey = ['media', config.testId, section.dataset.section || ''].join('|');
    if (initializedMediaSections.get(section) === sectionKey) {
      return;
    }
    initializedMediaSections.set(section, sectionKey);

    var assignment = getAssignment(config);
    if (!assignment) {
      return;
    }

    publishAssignmentOnce(config, assignment);
    reportArm(config, assignment);
    activateMediaTest(section, config, assignment);
  }

  function initMediaTests(root) {
    var mediaTests = getGlobalMediaTests();
    if (!mediaTests.length) {
      return;
    }

    selectAll(root || document, '[data-ab-media-test]').forEach(function (section) {
      try {
        initMediaSection(section, mediaTests);
      } catch (error) {
        console.warn('[product-ab-test]', 'Media test init failed', error);
      }
    });
  }

  function init(root) {
    var tests = getGlobalTests();
    var target = root || document;

    selectAll(target, '[data-ab-test-section]').forEach(function (section) {
      initSection(section, tests);
    });
    initCards(target, tests);
    initMediaTests(target);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('elanora:ab-content-updated', function (event) {
    init((event.detail && event.detail.root) || document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
})();
